import { useState, useEffect, useCallback, useMemo } from "react";
import * as tasksService from "../services/tasksService";

/**
 * Hook principal del módulo de tareas.
 *
 * Soporta dos vistas:
 *   - "my":  GET /tasks/my-tasks?employeeId=X (incluye myRole)
 *   - "all": GET /tasks (admin/manager)
 *
 * Mantiene tasks + stats sincronizados y expone CRUD completo.
 *
 * @param {Object} args
 * @param {"my"|"all"} args.view
 * @param {number} args.employeeId - Requerido si view = "my"
 * @param {Object} [args.filters] - status, priorityId, categoryId, locationId
 *                                  + (en view "all") assigneeId, creatorId, cipId, relatedType
 */
export default function useTasksModule({ view = "my", employeeId, filters = {} }) {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const refresh = useCallback(async () => {
    if (view === "my" && !employeeId) return;
    setLoading(true);
    setError(null);
    try {
      const statsFilters = {
        locationId: filters.locationId,
        ...(view === "my" ? { assigneeId: employeeId } : {}),
      };
      const [tasksData, statsData] = await Promise.all([
        view === "my"
          ? tasksService.getMyTasks(employeeId, filters)
          : tasksService.getTasks(filters),
        tasksService.getTaskStats(statsFilters),
      ]);
      setTasks(tasksData);
      setStats(statsData);
    } catch (err) {
      console.error("[useTasksModule] refresh error:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
    // filtersKey memoiza el JSON.stringify(filters) para evitar refetch en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, employeeId, filtersKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createTask = useCallback(
    async (taskData) => {
      const result = await tasksService.createTask({
        ...taskData,
        creatorId: taskData.creatorId || employeeId,
      });
      await refresh();
      return result;
    },
    [employeeId, refresh]
  );

  const updateTask = useCallback(
    async (taskId, taskData) => {
      const result = await tasksService.updateTask(taskId, {
        ...taskData,
        employeeId: taskData.employeeId || employeeId,
      });
      await refresh();
      return result;
    },
    [employeeId, refresh]
  );

  const completeTask = useCallback(
    async (taskId) => {
      const result = await tasksService.completeTask(taskId, employeeId);
      await refresh();
      return result;
    },
    [employeeId, refresh]
  );

  const updateStatus = useCallback(
    async (taskId, newStatus) => {
      const result = await tasksService.updateTaskStatus(taskId, newStatus, employeeId);
      await refresh();
      return result;
    },
    [employeeId, refresh]
  );

  const deleteTask = useCallback(
    async (taskId) => {
      const result = await tasksService.deleteTask(taskId, employeeId);
      await refresh();
      return result;
    },
    [employeeId, refresh]
  );

  const getTaskById = useCallback(
    (taskId) => tasksService.getTaskById(taskId),
    []
  );

  return {
    tasks,
    stats,
    loading,
    error,
    refresh,
    createTask,
    updateTask,
    completeTask,
    updateStatus,
    deleteTask,
    getTaskById,
  };
}
